import type { ApiResponse } from '@shared/types';
import { getIdToken } from './firebase';

// Empty in dev (Vite proxies /api to :4100); set to the Cloud Run URL when
// building for production (see scripts/deploy-client.sh).
const API_URL = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
  }
}

export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  const token = await getIdToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}/api${path}`, { ...options, headers });
  const body = (await res.json().catch(() => null)) as ApiResponse<T> | null;

  if (!res.ok || !body?.success) {
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return body.data as T;
}

// ── Envelope-style client (Outclose convention) ────────────────────────────
// The hooks ported from the Outclose repo expect the full `{ success, data,
// error }` envelope back (throwing only on non-2xx), so this mirrors the main
// repo's `api` object rather than the unwrapping `request()` above.

async function envelopeRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  const token = await getIdToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}/api${path}`, { ...options, headers });
  const body = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!res.ok) {
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return body as ApiResponse<T>;
}

export const api = {
  get: <T>(endpoint: string, options: RequestInit = {}) =>
    envelopeRequest<T>(endpoint, { method: 'GET', ...options }),
  post: <T>(endpoint: string, body?: unknown) =>
    envelopeRequest<T>(endpoint, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(endpoint: string, body?: unknown) =>
    envelopeRequest<T>(endpoint, { method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(endpoint: string, body?: unknown) =>
    envelopeRequest<T>(endpoint, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T>(endpoint: string, body?: unknown) =>
    envelopeRequest<T>(endpoint, { method: 'DELETE', body: body !== undefined ? JSON.stringify(body) : undefined }),
};
